import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, Calendar, Clock, ChevronDown } from 'lucide-react';
import SEOHead from '../../components/SEOHead';
import BlogNotFound from '../../components/BlogNotFound';
import { getBlogBySlug } from '../../data/blogs';

const AgentLightning = () => {
  const [activeSection, setActiveSection] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const blog = getBlogBySlug('agent-lightning-self-correcting-ai');

  useEffect(() => {
    setIsDesktop(window.innerWidth >= 1100);

    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);

      const sections = document.querySelectorAll('h2[id]');
      let current = '';

      sections.forEach(section => {
        const sectionTop = section.getBoundingClientRect().top;
        if (sectionTop <= 150) {
          current = section.getAttribute('id') || '';
        }
      });

      setActiveSection(current);
    };

    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1100);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -120;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      setTocOpen(false);
    }
  };

  const tableOfContents = [
    { id: 'context', title: '1. Context' },
    { id: 'problem', title: '2. Problem Statement' },
    { id: 'framework', title: '3. Core Framework: Agent Lightning' },
    { id: 'comparison', title: '4. Comparative Analysis' },
    { id: 'use-cases', title: '5. Business Use Cases' },
    { id: 'benefits', title: '6. Benefits & Outcomes' },
    { id: 'challenges', title: '7. Challenges & Realities' },
    { id: 'future', title: '8. Future Outlook' },
    { id: 'conclusion', title: '9. Conclusion' },
    { id: 'faq', title: '10. Frequently Asked Questions' }
  ];

  const faqData = [
    {
      question: 'What is Agent Lightning and how does it differ from traditional AI training?',
      answer: 'Agent Lightning is an open-source reinforcement learning framework by Microsoft that enables AI agents to continuously learn from their operational experiences. Unlike traditional AI training that requires manual retraining when errors occur, Agent Lightning allows agents to autonomously improve by learning from mistakes in real-time through reinforcement learning algorithms.'
    },
    {
      question: 'Can Agent Lightning work with my existing AI agents built on LangChain or AutoGen?',
      answer: 'Yes! Agent Lightning is designed to be platform-agnostic and integrates seamlessly with existing AI agent frameworks including LangChain, AutoGen, and OpenAI SDK without requiring extensive code rewrites. It acts as a sophisticated training layer that plugs into your current agent infrastructure.'
    },
    {
      question: 'What kind of performance improvements can I expect from implementing Agent Lightning?',
      answer: 'Early results show significant improvements: customer support bots increased resolution rates from 68% to 85%, and code assistants improved accuracy from 45% to 72%. The actual improvements depend on your specific use case, data quality, and how well reward signals are defined.'
    },
    {
      question: 'How do I define reward signals for Agent Lightning?',
      answer: 'Reward signals are predefined success metrics that score agent decisions. For customer support, this might be resolution rate or customer satisfaction scores. For code assistants, it could be code quality metrics or developer feedback. Clear, unambiguous reward signals are crucial for effective learning.'
    },
    {
      question: 'What are the main challenges in implementing Agent Lightning?',
      answer: 'Key challenges include: initial setup and configuration complexity, defining accurate reward signals that prevent unintended behaviors, requiring sufficient interaction data for meaningful learning, and the need for ongoing monitoring to ensure alignment with business objectives and prevent bias amplification.'
    },
    {
      question: 'How much data does Agent Lightning need to start showing improvements?',
      answer: 'Agent Lightning requires a sufficient volume of interaction data for its reinforcement learning algorithms to identify patterns. The exact amount varies by use case, but generally, more complex tasks require more data. Starting with high-frequency, well-defined tasks helps accelerate the learning process.'
    },
    {
      question: 'Is Agent Lightning suitable for small businesses or only enterprises?',
      answer: 'While Agent Lightning offers the most dramatic ROI for enterprises with high-volume AI operations, small businesses with repetitive AI-driven tasks can also benefit. The key is having enough operational data and clear success metrics to train the reinforcement learning system effectively.'
    },
    {
      question: 'What is the future roadmap for Agent Lightning in 2026?',
      answer: 'Over the next 12 months, expect a shift from static "deploy-and-forget" AI agents to dynamic, self-optimizing entities. Innovation will focus on advanced reinforcement learning techniques, sophisticated feedback loops, autonomous goal adaptation, and anomaly detection, making continuous learning the industry standard.'
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "headline": blog?.title,
        "description": blog?.excerpt,
        "image": blog?.featuredImage,
        "datePublished": blog?.publishDate,
        "dateModified": blog?.publishDate,
        "author": {
          "@type": "Organization",
          "name": "Agility"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Agility",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.agilitytech.ai/agility hex fonts.png"
          }
        },
        "keywords": blog?.seoKeywords.join(', '),
        "articleBody": "Comprehensive guide to Microsoft's Agent Lightning framework for building self-correcting AI agents through reinforcement learning.",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://www.agilitytech.ai/insights/${blog?.slug}`
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      }
    ]
  };

  if (!blog) return <BlogNotFound />;

  return (
    <div className="min-h-screen" style={{ background: '#050505' }}>
      <SEOHead
        title={`${blog?.title} | Agility Blog`}
        description={blog?.excerpt}
        keywords={blog?.seoKeywords.join(', ')}
        url={`https://www.agilitytech.ai/insights/${blog?.slug}`}
        image={blog?.featuredImage}
        type="article"
        article={true}
        author={blog?.author}
        publishedTime={new Date(blog?.publishDate || '').toISOString()}
        modifiedTime={new Date(blog?.publishDate || '').toISOString()}
        structuredData={structuredData}
      />

      {/* Blog Hero Section */}
      <section className="pt-32 pb-16" style={{ background: 'linear-gradient(to bottom, #0B0B0B, #050505)' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" style={{ maxWidth: '1200px' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            {/* Category Tag */}
            <div className="mb-6">
              <span style={{
                display: 'inline-block',
                padding: '0.5rem 1rem',
                background: 'rgba(20, 184, 166, 0.15)',
                color: '#14b8a6',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: '500',
                border: '1px solid rgba(20, 184, 166, 0.3)'
              }}>
                {blog.category}
              </span>
            </div>

            {/* Title */}
            <h1 style={{
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              fontFamily: 'Sora, sans-serif',
              fontWeight: '700',
              color: '#ffffff',
              marginBottom: '1.5rem',
              lineHeight: '1.2',
              letterSpacing: '-0.02em'
            }}>
              {blog.title}
            </h1>

            {/* Meta Info */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', color: '#9ca3af', marginBottom: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Calendar size={16} />
                <span style={{ fontSize: '0.875rem' }}>{new Date(blog.publishDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Clock size={16} />
                <span style={{ fontSize: '0.875rem' }}>{blog.readTime} read</span>
              </div>
            </div>

            {/* Excerpt */}
            <p style={{
              fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
              color: '#d1d5db',
              lineHeight: '1.75',
              marginBottom: '2rem'
            }}>
              {blog.excerpt}
            </p>

            {/* Tags */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {blog.tags.map((tag, index) => (
                <span key={index} style={{
                  padding: '0.25rem 0.75rem',
                  background: '#121212',
                  color: '#14b8a6',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  border: '1px solid #262626'
                }}>
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <section style={{ background: '#050505', paddingBottom: '5rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem', display: 'flex', minHeight: '100vh', gap: '3rem', flexDirection: isDesktop ? 'row' : 'column' }}>
          {/* Sidebar TOC - Desktop */}
          {isDesktop && (
            <div style={{
              position: 'sticky',
              top: '100px',
              height: 'fit-content',
              width: '260px',
              flexShrink: 0,
              alignSelf: 'flex-start',
              zIndex: 999
            }}>
              <h3 style={{
                color: '#ffffff',
                fontWeight: '600',
                marginBottom: '1rem',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>Table of Contents</h3>
              <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {tableOfContents.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      fontSize: '0.875rem',
                      padding: '0.5rem 1rem',
                      borderRadius: '0.5rem',
                      transition: 'all 0.2s',
                      borderLeft: activeSection === item.id ? '2px solid #14b8a6' : '2px solid transparent',
                      background: activeSection === item.id ? 'rgba(20,184,166,0.15)' : 'transparent',
                      color: activeSection === item.id ? '#14b8a6' : '#9ca3af',
                      cursor: 'pointer',
                      border: 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (activeSection !== item.id) {
                        e.currentTarget.style.color = '#d1d5db';
                        e.currentTarget.style.background = '#121212';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeSection !== item.id) {
                        e.currentTarget.style.color = '#9ca3af';
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    {item.title}
                  </button>
                ))}
              </nav>
            </div>
          )}

          {/* Mobile TOC */}
          {!isDesktop && (
            <div style={{
              width: '100%',
              marginBottom: '2rem'
            }}>
              <div style={{
                background: '#121212',
                borderRadius: '12px',
                border: '1px solid #262626',
                padding: '1rem'
              }}>
                <button
                  onClick={() => setTocOpen(!tocOpen)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'none',
                    border: 'none',
                    color: '#ffffff',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  <span>Table of Contents</span>
                  <ChevronDown style={{
                    transform: tocOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }} />
                </button>
                {tocOpen && (
                  <nav style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {tableOfContents.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '0.5rem 1rem',
                          background: 'none',
                          border: 'none',
                          color: activeSection === item.id ? '#14b8a6' : '#9ca3af',
                          fontSize: '0.875rem',
                          cursor: 'pointer'
                        }}
                      >
                        {item.title}
                      </button>
                    ))}
                  </nav>
                )}
              </div>
            </div>
          )}

          {/* Article Content */}
          <div style={{ flex: 1 }}>
            <article style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
              {/* Section 1 - Context */}
              <h2 id="context" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Context
              </h2>
              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                The landscape of Artificial Intelligence is rapidly evolving, with a strong emphasis on developing and deploying AI agents that can automate complex tasks and interact intelligently. From advanced machine learning models to sophisticated deep learning networks and natural language processing capabilities, organizations are investing heavily in building AI solutions. However, a significant hurdle persists: ensuring these AI agents continuously improve and adapt, moving beyond their initial deployment to become truly resilient and effective. The ability for AI agents to learn from their operational experiences and self-correct their mistakes is no longer a luxury but a critical necessity for maintaining a competitive edge and delivering sustained value.
              </p>

              {/* Section 2 - Problem Statement */}
              <h2 id="problem" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Problem Statement
              </h2>
              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Current methods for developing AI agents often involve extensive initial training and deployment, but they frequently lack robust mechanisms for continuous, autonomous improvement. This leads to a fundamental operational inefficiency: if an AI agent makes a mistake, it tends to repeat that error indefinitely until manual intervention, retraining, or fine-tuning occurs. Such repetitive errors translate directly into suboptimal performance, increased operational costs due to human oversight and correction, diminished customer satisfaction in support scenarios, and reduced accuracy in critical applications like code assistance. The absence of a seamless "learn from mistakes" pipeline creates a bottleneck, preventing AI agents from reaching their full potential and incurring significant hidden costs for businesses.
              </p>

              {/* Section 3 - Core Framework */}
              <h2 id="framework" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Core Framework: Agent Lightning
              </h2>

              <h3 style={{
                color: '#ffffff',
                fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1rem',
                marginTop: '2rem'
              }}>Definition</h3>
              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Agent Lightning is an innovative, open-source framework developed by Microsoft, designed to empower AI agents with the ability to learn and improve autonomously through reinforcement learning. It acts as a sophisticated training layer that allows any AI agent to develop and refine its skills by learning from its own operational experiences and decisions, without requiring extensive code rewrites.
              </p>

              <h3 style={{
                color: '#ffffff',
                fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1rem',
                marginTop: '2rem'
              }}>How it Works</h3>
              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Agent Lightning integrates seamlessly with existing AI agent platforms, regardless of whether they are built with popular frameworks like LangChain, AutoGen, or OpenAI's SDK. Once plugged in, Agent Lightning observes the agent's actions and decisions in real-time. It then records every decision made and assigns a score based on predefined success metrics or reward signals. This invaluable data detailing both successes and errors is subsequently fed into powerful reinforcement learning algorithms. These algorithms process the experiential data, identify patterns, and implement improvements that actually enhance the agent's performance over time, effectively teaching the AI agent to learn from its mistakes and optimize its future decisions.
              </p>

              {/* IMAGE 1: Reinforcement Learning Workflow */}
              <div style={{
                margin: '2.5rem 0',
                background: '#121212',
                borderRadius: '12px',
                padding: '1.5rem',
                border: '1px solid #262626'
              }}>
                <p style={{
                  color: '#14b8a6',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Reinforcement Learning Workflow: Continuous Agent Improvement
                </p>
                <div style={{
                  width: '100%',
                  height: 'clamp(240px, 40vw, 320px)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, rgba(20,184,166,0.08), rgba(0,150,136,0.03)), url('https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&q=80')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }} />
                <p style={{
                  color: '#9ca3af',
                  fontSize: '0.875rem',
                  marginTop: '1rem',
                  fontStyle: 'italic',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Agent Lightning observes agent decisions in real-time, scores outcomes based on reward signals, and feeds experiential data into reinforcement learning algorithms. This creates a continuous feedback loop where the AI learns from both successes and failures, progressively improving its decision-making capabilities.
                </p>
              </div>

              <h3 style={{
                color: '#ffffff',
                fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1rem',
                marginTop: '2rem'
              }}>Limitations</h3>
              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                While Agent Lightning offers powerful capabilities, its effectiveness can be influenced by several factors. Initial setup requires careful configuration to ensure proper integration with existing agents and accurate observation of decisions. Defining clear, unambiguous reward signals is crucial for effective reinforcement learning; poorly designed rewards can lead to unintended learning behaviors or suboptimal performance. Furthermore, like any data-driven system, Agent Lightning requires a sufficient volume of interaction data for its reinforcement learning algorithms to identify patterns and make meaningful improvements. Organizations also need to be prepared for the ongoing monitoring and validation of agent behavior as it learns, to ensure alignment with business objectives and prevent the amplification of biases present in initial scoring mechanisms.
              </p>

              {/* Section 4 - Comparative Analysis */}
              <h2 id="comparison" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Comparative Analysis
              </h2>
              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                To understand the transformative potential of Agent Lightning, it's useful to compare its approach to traditional AI agent development and improvement methods.
              </p>

              {/* IMAGE 2: Traditional vs Modern AI Training */}
              <div style={{
                margin: '2.5rem 0',
                background: '#121212',
                borderRadius: '12px',
                padding: '1.5rem',
                border: '1px solid #262626'
              }}>
                <p style={{
                  color: '#14b8a6',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Evolution of AI Training: Manual vs. Autonomous Learning
                </p>
                <div style={{
                  width: '100%',
                  height: 'clamp(240px, 40vw, 320px)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, rgba(20,184,166,0.08), rgba(0,150,136,0.03)), url('https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }} />
                <p style={{
                  color: '#9ca3af',
                  fontSize: '0.875rem',
                  marginTop: '1rem',
                  fontStyle: 'italic',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  The shift from traditional manual retraining cycles to Agent Lightning's autonomous improvement represents a paradigm change in AI development. While conventional methods require human-driven intervention for every update, reinforcement learning enables continuous, self-directed optimization without code modifications.
                </p>
              </div>

              <div className="table-wrapper" style={{
                overflowX: 'auto',
                borderRadius: '12px',
                border: '1px solid #262626',
                marginBottom: '2rem'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                  <thead style={{ background: '#1A1A1A' }}>
                    <tr>
                      <th style={{
                        textAlign: 'left',
                        padding: '1rem',
                        color: '#ffffff',
                        fontWeight: '600',
                        borderBottom: '1px solid #262626',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                      }}>Feature</th>
                      <th style={{
                        textAlign: 'left',
                        padding: '1rem',
                        color: '#ffffff',
                        fontWeight: '600',
                        borderBottom: '1px solid #262626',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                      }}>Agent Lightning (RL-based Continuous Improvement)</th>
                      <th style={{
                        textAlign: 'left',
                        padding: '1rem',
                        color: '#ffffff',
                        fontWeight: '600',
                        borderBottom: '1px solid #262626',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                      }}>Traditional AI Agent Development</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Learning Mechanism', 'Reinforcement Learning from live interactions and scored decisions', 'Supervised learning, rule-based systems, manual fine-tuning post-deployment'],
                      ['Adaptability & Improvement', 'Continuous, autonomous self-correction and performance optimization', 'Requires manual intervention, data collection, and explicit retraining'],
                      ['Integration Complexity', 'Plugs into existing agents (LangChain, AutoGen, OpenAI SDK) without code rewrite', 'Often requires significant code modifications or data re-labeling for updates'],
                      ['Error Correction', 'Actively learns from mistakes to prevent repetition', 'Mistakes can repeat until human-driven intervention and redeployment'],
                      ['Iteration Speed', 'Rapid, automated improvement cycles based on real-time feedback', 'Slower, human-dependent iteration cycles'],
                      ['Cost of Improvement', 'Primarily computational resources for RL; reduced human oversight', 'Significant human labor for data annotation, model tuning, and deployment']
                    ].map((row, idx) => (
                      <tr key={idx} style={{ transition: 'background 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(20, 184, 166, 0.02)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                        <td style={{
                          padding: '1rem',
                          color: '#14b8a6',
                          fontWeight: '600',
                          borderBottom: idx < 5 ? '1px solid #262626' : 'none',
                          fontFamily: 'Inter, sans-serif',
                          fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                        }}>{row[0]}</td>
                        <td style={{
                          padding: '1rem',
                          color: '#e5e7eb',
                          borderBottom: idx < 5 ? '1px solid #262626' : 'none',
                          fontFamily: 'Inter, sans-serif',
                          fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                        }}>{row[1]}</td>
                        <td style={{
                          padding: '1rem',
                          color: '#e5e7eb',
                          borderBottom: idx < 5 ? '1px solid #262626' : 'none',
                          fontFamily: 'Inter, sans-serif',
                          fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                        }}>{row[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Section 5 - Business Use Cases */}
              <h2 id="use-cases" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Business Use Cases
              </h2>
              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Agent Lightning's ability to drive self-improvement in AI agents unlocks significant value across various industries.
              </p>

              {/* IMAGE 3: Customer Support Dashboard */}
              <div style={{
                margin: '2.5rem 0',
                background: '#121212',
                borderRadius: '12px',
                padding: '1.5rem',
                border: '1px solid #262626'
              }}>
                <p style={{
                  color: '#14b8a6',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Real-World Impact: Customer Support Performance Metrics
                </p>
                <div style={{
                  width: '100%',
                  height: 'clamp(240px, 40vw, 320px)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, rgba(20,184,166,0.08), rgba(0,150,136,0.03)), url('https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }} />
                <p style={{
                  color: '#9ca3af',
                  fontSize: '0.875rem',
                  marginTop: '1rem',
                  fontStyle: 'italic',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Customer support bots powered by Agent Lightning demonstrate measurable improvements in resolution rates, jumping from 68% to 85% through continuous learning from customer interactions and feedback. The framework enables bots to adapt to edge cases and evolving customer needs autonomously.
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                <div style={{
                  background: '#121212',
                  borderLeft: '3px solid #14b8a6',
                  borderRadius: '12px',
                  padding: '1.5rem'
                }}>
                  <h3 style={{
                    color: '#ffffff',
                    fontSize: 'clamp(1.125rem, 2.5vw, 1.25rem)',
                    fontWeight: '600',
                    marginBottom: '0.75rem',
                    fontFamily: 'Sora, sans-serif'
                  }}>Industry: Customer Support</h3>
                  <p style={{
                    color: '#e5e7eb',
                    marginBottom: '1rem',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                  }}>
                    <strong style={{ color: '#ffffff' }}>Problem:</strong> Customer support bots often struggle with edge cases or evolving customer queries, leading to repetitive failures and low resolution rates, impacting customer satisfaction and increasing the need for human agent escalation.
                  </p>
                  <p style={{
                    color: '#e5e7eb',
                    fontWeight: '600',
                    marginBottom: '0.5rem',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                  }}>Value:</p>
                  <p style={{
                    color: '#e5e7eb',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                    marginBottom: '0.5rem'
                  }}>
                    By continuously learning from customer interactions and agent feedback, bots can significantly improve their ability to understand and resolve issues. Early results show customer support bots jumped from a <strong style={{ color: '#14b8a6' }}>68% resolution rate to an impressive 85% resolution rate</strong>. This translates to reduced call volumes, lower operational costs, and enhanced customer experience.
                  </p>
                </div>

                <div style={{
                  background: '#121212',
                  borderLeft: '3px solid #14b8a6',
                  borderRadius: '12px',
                  padding: '1.5rem'
                }}>
                  <h3 style={{
                    color: '#ffffff',
                    fontSize: 'clamp(1.125rem, 2.5vw, 1.25rem)',
                    fontWeight: '600',
                    marginBottom: '0.75rem',
                    fontFamily: 'Sora, sans-serif'
                  }}>Industry: Software Development (Code Assistance)</h3>
                  <p style={{
                    color: '#e5e7eb',
                    marginBottom: '1rem',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                  }}>
                    <strong style={{ color: '#ffffff' }}>Problem:</strong> Code assistants, while helpful, can produce inaccurate suggestions or generate suboptimal code, requiring developers to spend extra time correcting errors and verifying outputs, hindering productivity.
                  </p>
                  <p style={{
                    color: '#e5e7eb',
                    fontWeight: '600',
                    marginBottom: '0.5rem',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                  }}>Value:</p>
                  <p style={{
                    color: '#e5e7eb',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                  }}>
                    Agent Lightning enables code assistants to learn from developer feedback, code reviews, and successful code implementations. This continuous learning drastically improves their accuracy. Initial reports indicate code assistants saw their accuracy <strong style={{ color: '#14b8a6' }}>soar from 45% to 72%</strong>, leading to faster development cycles, higher code quality, and more efficient software engineering teams.
                  </p>
                </div>
              </div>

              {/* Section 6 - Benefits & Outcomes */}
              <h2 id="benefits" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Benefits & Outcomes
              </h2>

              <h3 style={{
                color: '#ffffff',
                fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1rem',
                marginTop: '2rem'
              }}>Technical</h3>
              <ul style={{ marginBottom: '2rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {[
                  { title: 'Seamless Reinforcement Learning Integration:', desc: 'Integrates advanced reinforcement learning capabilities into existing AI agents without necessitating a complete rewrite of their underlying code.' },
                  { title: 'Platform Agnostic:', desc: 'Works effectively with agents built on diverse frameworks such as LangChain, AutoGen, and OpenAI\'s SDK, offering broad compatibility.' },
                  { title: 'Observational Learning Engine:', desc: 'Features a robust mechanism to observe agent decisions, record interactions, and score outcomes, providing the essential data for continuous improvement.' },
                  { title: 'Data-Driven Optimization:', desc: 'Leverages experiential data to fuel reinforcement learning algorithms, leading to more intelligent and adaptive agent behavior over time.' }
                ].map((item, idx) => (
                  <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', listStyle: 'none' }}>
                    <div style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: '#14b8a6',
                      marginTop: '10px',
                      marginRight: '12px',
                      flexShrink: 0
                    }} />
                    <span style={{ color: '#e5e7eb', fontSize: 'clamp(1rem, 2vw, 1.125rem)', lineHeight: '1.75', fontFamily: 'Inter, sans-serif' }}>
                      <strong style={{ color: '#ffffff' }}>{item.title}</strong> {item.desc}
                    </span>
                  </li>
                ))}
              </ul>

              <h3 style={{
                color: '#ffffff',
                fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1rem',
                marginTop: '2rem'
              }}>Business</h3>
              <ul style={{ marginBottom: '2rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {[
                  { title: 'Dramatic Performance Improvements:', desc: 'Demonstrated significant increases in key performance indicators, such as customer support resolution rates (from 68% to 85%) and code assistant accuracy (from 45% to 72%).' },
                  { title: 'Reduced Operational Inefficiencies:', desc: 'Minimizes the occurrence of repetitive errors and the need for manual corrections, freeing up human resources for more complex tasks.' },
                  { title: 'Enhanced Customer Satisfaction:', desc: 'By enabling agents to provide more accurate and effective support, it directly improves the customer experience.' },
                  { title: 'Accelerated Innovation Cycles:', desc: 'Facilitates faster iteration and deployment of more capable AI agents, allowing businesses to adapt quickly to changing demands and market conditions.' },
                  { title: 'Cost Savings:', desc: 'Reduces costs associated with manual retraining, error handling, and customer escalations.' }
                ].map((item, idx) => (
                  <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', listStyle: 'none' }}>
                    <div style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: '#14b8a6',
                      marginTop: '10px',
                      marginRight: '12px',
                      flexShrink: 0
                    }} />
                    <span style={{ color: '#e5e7eb', fontSize: 'clamp(1rem, 2vw, 1.125rem)', lineHeight: '1.75', fontFamily: 'Inter, sans-serif' }}>
                      <strong style={{ color: '#ffffff' }}>{item.title}</strong> {item.desc}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Section 7 - Challenges */}
              <h2 id="challenges" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Challenges & Realities
              </h2>
              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Implementing Agent Lightning, while transformative, comes with its own set of challenges and realities. Successfully deploying and optimizing the framework requires more than just technical integration. Organizations must invest time in carefully defining robust reward functions and scoring mechanisms that accurately reflect desired outcomes and align with business objectives. There's an initial learning curve associated with understanding and fine-tuning reinforcement learning parameters for optimal performance. Additionally, ensuring data privacy and security when observing agent interactions is paramount. As an open-source framework, Agent Lightning benefits from community support, but organizations should be prepared for internal expertise development and self-reliance for specific customizations and troubleshooting.
              </p>

              {/* Section 8 - Future Outlook */}
              <h2 id="future" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Future Outlook
              </h2>
              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Over the next 12 months, the trend will strongly favor AI agent frameworks that prioritize continuous learning and self-improvement. We can expect to see a significant shift from static, "deploy-and-forget" AI agents to dynamic, self-optimizing entities. The emphasis will move beyond merely deploying AI to actively cultivating agents that become smarter and more capable with every interaction. This will drive further innovation in reinforcement learning techniques tailored for complex agent environments, leading to more sophisticated feedback loops, advanced anomaly detection, and even autonomous goal adaptation. The market will increasingly demand solutions that promise not just AI deployment, but AI evolution.
              </p>

              {/* Section 9 - Conclusion */}
              <h2 id="conclusion" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Conclusion
              </h2>
              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Agent Lightning stands out as a pivotal advancement in the realm of artificial intelligence, empowering AI agents to transcend their initial programming and truly learn from experience. By seamlessly integrating reinforcement learning into existing agent ecosystems, it offers a pragmatic and powerful solution to the pervasive problem of repetitive errors and static performance. The impressive early results in customer support and code assistance underscore its immense value, demonstrating that cultivating self-improving AI is not merely an aspirational goal but an achievable reality, capable of driving substantial operational efficiencies and superior outcomes.
              </p>

              {/* FAQ Section */}
              <h2 id="faq" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Frequently Asked Questions
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '3rem' }}>
                {faqData.map((faq, idx) => (
                  <div key={idx} style={{
                    background: '#121212',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    border: '1px solid #262626',
                    transition: 'border-color 0.3s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = '#14b8a6'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = '#262626'}>
                    <h3 style={{
                      color: '#ffffff',
                      fontSize: 'clamp(1.125rem, 2.5vw, 1.25rem)',
                      fontWeight: '600',
                      marginBottom: '0.75rem',
                      fontFamily: 'Sora, sans-serif',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem'
                    }}>
                      <span style={{ color: '#14b8a6', flexShrink: 0 }}>Q{idx + 1}.</span>
                      <span>{faq.question}</span>
                    </h3>
                    <p style={{
                      color: '#e5e7eb',
                      fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                      lineHeight: '1.75',
                      marginLeft: '2rem',
                      fontFamily: 'Inter, sans-serif'
                    }}>
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>

              {/* CTA Section */}
              <div style={{
                marginTop: '3rem',
                background: '#121212',
                borderRadius: '16px',
                padding: 'clamp(1.5rem, 4vw, 2rem)',
                border: '1px solid #262626',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '384px',
                  height: '384px',
                  background: 'rgba(20, 184, 166, 0.1)',
                  borderRadius: '50%',
                  filter: 'blur(80px)'
                }}></div>
                <div style={{ position: 'relative', zIndex: 10, textAlign: 'center' }}>
                  <h3 style={{
                    fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                    fontWeight: '700',
                    color: '#ffffff',
                    marginBottom: '1rem',
                    fontFamily: 'Sora, sans-serif'
                  }}>
                    Ready to Unlock Your AI's Full Potential?
                  </h3>
                  <p style={{
                    color: '#e5e7eb',
                    fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                    marginBottom: '1.5rem',
                    maxWidth: '600px',
                    margin: '0 auto 1.5rem',
                    fontFamily: 'Inter, sans-serif'
                  }}>
                    Discover how Microsoft's Agent Lightning can transform your operational efficiency and accelerate your business objectives. Contact us today for a Proof of Concept (POC) or a personalized consultation to explore its application within your enterprise.
                  </p>
                  <Link
                    to="/contact"
                    style={{
                      display: 'inline-block',
                      padding: 'clamp(0.625rem, 2vw, 0.75rem) clamp(1.25rem, 3vw, 1.5rem)',
                      border: '2px solid #14b8a6',
                      color: '#14b8a6',
                      borderRadius: '8px',
                      fontWeight: '600',
                      textDecoration: 'none',
                      transition: 'all 0.3s',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#14b8a6';
                      e.currentTarget.style.color = '#ffffff';
                      e.currentTarget.style.boxShadow = '0 0 20px rgba(20, 184, 166, 0.25)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#14b8a6';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    Schedule Your Consultation Today
                  </Link>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          style={{
            position: 'fixed',
            bottom: 'clamp(1.5rem, 4vw, 2rem)',
            right: 'clamp(1.5rem, 4vw, 2rem)',
            width: 'clamp(44px, 10vw, 48px)',
            height: 'clamp(44px, 10vw, 48px)',
            background: '#121212',
            border: '2px solid #262626',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#14b8a6',
            cursor: 'pointer',
            transition: 'all 0.3s',
            zIndex: 50
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#14b8a6';
            e.currentTarget.style.boxShadow = '0 0 20px rgba(20, 184, 166, 0.25)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#262626';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <ArrowUp size={20} />
        </button>
      )}
    </div>
  );
};

export default AgentLightning;
